import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Crown, Users, Copy, Share2, Trophy, Trash2, X,
  ExternalLink, Loader2, ArrowLeft, ChevronDown, Medal, Minus, Settings, 
  Globe, Lock, MessageCircle
} from "lucide-react";
import LeagueAdminPanel from "../components/LeagueAdminPanel";
import UserProfileCard from "../components/UserProfileCard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LeagueDetailPage() {
  const { leagueId } = useParams();
  const { user, dbUser, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Admin panel
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  // User profile modal
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null);

  // Mode: "season" | "matchday" | "per-match" | "weekly"
  const [mode, setMode] = useState("season");

  // Per-match data
  const [availableMatches, setAvailableMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [matchStandings, setMatchStandings] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);

  // Weekly data
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weeklyStandings, setWeeklyStandings] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [loadingWeekly, setLoadingWeekly] = useState(false);

  const fetchLeagueDetails = useCallback(async () => {
    if (!user) return;
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API}/leagues/${leagueId}`, { headers });
      setLeague(res.data.league);
      setLeaderboard(res.data.leaderboard || []);
    } catch (e) {
      setError("Failed to load league details");
    } finally {
      setLoading(false);
    }
  }, [user, leagueId, getAuthHeader]);

  const fetchMatchList = useCallback(async () => {
    if (!user) return;
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API}/leagues/${leagueId}/match-standings`, { headers });
      setAvailableMatches(res.data.matches || []);
    } catch {
      // Silently fail — no per-match data available
    }
  }, [user, leagueId, getAuthHeader]);

  useEffect(() => {
    fetchLeagueDetails();
    fetchMatchList();
  }, [fetchLeagueDetails, fetchMatchList]);

  const fetchMatchStandings = async (matchId) => {
    if (!matchId) { setMatchStandings(null); return; }
    setLoadingMatch(true);
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API}/leagues/${leagueId}/match-standings/${matchId}`, { headers });
      setMatchStandings(res.data);
    } catch {
      setError("Failed to load match standings");
    } finally {
      setLoadingMatch(false);
    }
  };

  const fetchWeeklyStandings = async (weekNum) => {
    if (!weekNum) { setWeeklyStandings(null); return; }
    setLoadingWeekly(true);
    try {
      const headers = getAuthHeader();
      const res = await axios.get(
        `${API}/leagues/${leagueId}/weekly-standings/${weekNum}`,
        { headers }
      );
      setWeeklyStandings(res.data);
      setAvailableWeeks(res.data.available_weeks || []);
    } catch {
      // silent fail
    } finally {
      setLoadingWeekly(false);
    }
  };

  // Auto-load latest week when Weekly tab is selected
  useEffect(() => {
    if (mode !== "weekly") return;
    if (availableWeeks.length > 0) {
      const latest = selectedWeek || Math.max(...availableWeeks);
      if (!selectedWeek) setSelectedWeek(latest);
      fetchWeeklyStandings(latest);
    } else {
      // Bootstrap with week 1 to discover available_weeks
      fetchWeeklyStandings(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const leaveLeague = async () => {
    if (!window.confirm("Are you sure you want to leave this league?")) return;
    try {
      await axios.post(`${API}/leagues/${leagueId}/leave`, {}, { headers: getAuthHeader() });
      navigate("/my-leagues");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to leave league");
    }
  };

  const deleteLeague = async () => {
    if (!window.confirm("Delete this league permanently?")) return;
    try {
      await axios.delete(`${API}/leagues/${leagueId}`, { headers: getAuthHeader() });
      navigate("/leagues");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete league");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Code ${code} copied!`);
    setTimeout(() => setSuccess(""), 2000);
  };

  const shareWhatsApp = (code, name) => {
    const message = `Join my APL Mini League!\n\nLeague: *${name}*\nCode: *${code}*\n\nJoin here: ${window.location.origin}/leagues?join=${code}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Sort leaderboard
  const displayLeaderboard = mode === "matchday"
    ? [...leaderboard].sort((a, b) => (b.daily_total_points || 0) - (a.daily_total_points || 0))
    : leaderboard;

  if (!user) {
    return (
      <div className="text-center py-12">
        <Crown className="mx-auto text-slate-600 mb-4" size={48} />
        <p className="text-slate-400">Please login to view league details</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">League not found</p>
        <button onClick={() => navigate("/my-leagues")} className="mt-4 text-emerald-400 hover:underline">Back to Leagues</button>
      </div>
    );
  }

  const isCreator = dbUser?.id && league.created_by === dbUser.id;

  return (
    <div className="space-y-5 pb-24" data-testid="league-detail-page">
      {/* Back button */}
      <button
        onClick={() => navigate("/my-leagues")}
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        data-testid="back-to-leagues"
      >
        <ArrowLeft size={18} /> Back to Leagues
      </button>

      {/* League Header */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
            <Crown className="text-white" size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-white truncate" data-testid="league-name">{league.name}</h1>
              {league.visibility === "public" ? (
                <Globe size={16} className="text-emerald-400 flex-shrink-0" />
              ) : (
                <Lock size={16} className="text-slate-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-slate-400 text-sm flex items-center gap-2 mt-0.5">
              <Users size={14} /> {league.member_count || league.members?.length || 0}/{league.max_members || 5} members
              {isCreator && <span className="text-emerald-400">• Admin</span>}
            </p>
          </div>
          {isCreator && (
            <button
              onClick={() => setShowAdminPanel(true)}
              className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
              data-testid="admin-panel-btn"
            >
              <Settings size={20} className="text-emerald-400" />
            </button>
          )}
        </div>

        {/* Description */}
        {league.description && (
          <p className="mt-3 text-slate-400 text-sm">{league.description}</p>
        )}

        {/* WhatsApp Group Link */}
        {league.whatsapp_link && (
          <a
            href={league.whatsapp_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 flex items-center gap-2 bg-green-600/20 border border-green-500/30 rounded-lg px-3 py-2 text-green-400 text-sm hover:bg-green-600/30 transition-all"
          >
            <MessageCircle size={16} />
            Join WhatsApp Group
            <ExternalLink size={14} />
          </a>
        )}

        {/* Invite Code + Share */}
        <div className="mt-4 flex items-center justify-between bg-slate-900/50 rounded-xl p-3">
          <div>
            <p className="text-xs text-slate-500">Invite Code</p>
            <code className="text-emerald-400 font-mono text-lg" data-testid="league-code">{league.code}</code>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => copyCode(league.code)}
              className="p-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
              data-testid="copy-code-btn"
            >
              <Copy size={18} className="text-slate-300" />
            </button>
            <button
              onClick={() => shareWhatsApp(league.code, league.name)}
              className="p-2.5 bg-green-600 hover:bg-green-500 rounded-lg transition-all"
              data-testid="whatsapp-share-btn"
            >
              <Share2 size={18} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 text-sm flex items-center justify-between">
          {error}
          <button onClick={() => setError("")}><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 rounded-lg p-3 text-sm">
          {success}
        </div>
      )}

      {/* Mode Tabs */}
      <div className="flex bg-slate-800/50 border border-slate-700/50 rounded-xl p-1 gap-1" data-testid="league-mode-tabs">
        {[
          { key: "season", label: "Season", color: "bg-emerald-500 text-white" },
          { key: "matchday", label: "Match Day", color: "bg-amber-500 text-black" },
          { key: "per-match", label: "Per Match", color: "bg-cyan-500 text-white" },
          { key: "weekly", label: "Weekly", color: "bg-purple-500 text-white" },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setMode(tab.key)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
              mode === tab.key ? tab.color : "text-slate-400 hover:text-slate-200"
            }`}
            data-testid={`league-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Season / Match Day Standings */}
      {(mode === "season" || mode === "matchday") && (
        <div className="space-y-2" data-testid="league-standings">
          <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2 px-1">
            <Trophy size={16} />
            {mode === "matchday" ? "Match Day Cumulative" : "Season Standings"}
          </h3>
          {displayLeaderboard.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No members yet</p>
          ) : (
            displayLeaderboard.map((member, index) => {
              const pts = mode === "matchday" ? (member.daily_total_points || 0) : (member.total_points || 0);
              return (
                <div
                  key={member.id}
                  onClick={() => setSelectedProfileUserId(member.id)}
                  className={`flex items-center justify-between p-3.5 rounded-xl transition-all cursor-pointer hover:opacity-80 ${
                    index === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                    index === 1 ? 'bg-slate-400/10 border border-slate-400/30' :
                    index === 2 ? 'bg-orange-500/10 border border-orange-500/30' :
                    'bg-slate-800/50 border border-slate-700/30'
                  }`}
                  data-testid={`standing-row-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-slate-400 text-black' :
                      index === 2 ? 'bg-orange-500 text-black' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-white font-medium">{member.team_name || member.display_name || 'Unknown'}</span>
                  </div>
                  <span className={`font-mono font-semibold ${mode === "matchday" ? "text-amber-400" : "text-emerald-400"}`}>
                    {pts} pts
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Per-Match Standings */}
      {mode === "per-match" && (
        <div className="space-y-4" data-testid="per-match-section">
          {availableMatches.length === 0 ? (
            <div className="text-center py-8 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <Medal className="mx-auto text-slate-600 mb-3" size={40} />
              <p className="text-slate-400">No match results available yet</p>
              <p className="text-slate-500 text-sm mt-1">Per-match standings appear after daily points are calculated</p>
            </div>
          ) : (
            <>
              {/* Match Selector */}
              <div className="relative">
                <select
                  value={selectedMatchId}
                  onChange={(e) => {
                    setSelectedMatchId(e.target.value);
                    fetchMatchStandings(e.target.value);
                  }}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500"
                  data-testid="match-selector"
                >
                  <option value="">Select a match...</option>
                  {availableMatches.map(m => (
                    <option key={m.match_id} value={m.match_id}>
                      {m.team1_short || m.team1} vs {m.team2_short || m.team2} — {m.date} ({m.participants} played)
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>

              {/* Match Standings */}
              {loadingMatch && (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-cyan-400" size={32} />
                </div>
              )}

              {matchStandings && !loadingMatch && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2 px-1">
                    <Medal size={16} className="text-cyan-400" />
                    Match Standings
                  </h3>
                  {matchStandings.standings.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No league members played this match</p>
                  ) : (
                    matchStandings.standings.map((entry, index) => (
                      <div
                        key={entry.user_id}
                        onClick={() => setSelectedProfileUserId(entry.user_id)}
                        className={`flex items-center justify-between p-3.5 rounded-xl cursor-pointer hover:opacity-80 transition-all ${
                          index === 0 ? 'bg-cyan-500/10 border border-cyan-500/30' :
                          index === 1 ? 'bg-slate-400/10 border border-slate-400/30' :
                          index === 2 ? 'bg-orange-500/10 border border-orange-500/30' :
                          'bg-slate-800/50 border border-slate-700/30'
                        }`}
                        data-testid={`match-standing-row-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-cyan-500 text-black' :
                            index === 1 ? 'bg-slate-400 text-black' :
                            index === 2 ? 'bg-orange-500 text-black' :
                            'bg-slate-700 text-slate-300'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <span className="text-white font-medium">{entry.team_name}</span>
                            {entry.captain_name && (
                              <p className="text-slate-500 text-xs">C: {entry.captain_name}{entry.vc_name ? ` | VC: ${entry.vc_name}` : ""}</p>
                            )}
                          </div>
                        </div>
                        <span className="font-mono font-semibold text-cyan-400">{entry.points} pts</span>
                      </div>
                    ))
                  )}

                  {/* Members who didn't play */}
                  {matchStandings.not_played?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 mb-1.5 px-1">Didn't play this match:</p>
                      <div className="flex flex-wrap gap-2">
                        {matchStandings.not_played.map(np => (
                          <span key={np.user_id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-800/50 border border-slate-700/30 rounded-lg text-xs text-slate-500">
                            <Minus size={12} /> {np.team_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Weekly Standings */}
      {mode === "weekly" && (
        <div className="space-y-4" data-testid="weekly-section">
          <div className="relative">
            <select
              value={selectedWeek || ""}
              onChange={(e) => {
                const w = parseInt(e.target.value);
                setSelectedWeek(w);
                fetchWeeklyStandings(w);
              }}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 px-4 text-white appearance-none"
              data-testid="weekly-week-selector"
            >
              {availableWeeks.map(w => (
                <option key={w} value={w}>Week {w}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
          </div>

          {loadingWeekly ? (
            <p className="text-slate-400 text-center py-8">Loading...</p>
          ) : weeklyStandings?.standings?.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2 px-1">
                <Trophy size={16} />
                Week {weeklyStandings.week_number} Standings
              </h3>
              {weeklyStandings.standings.map((member, index) => {
                const rc = member.rank_change;
                const rv = member.rank_change_value;
                return (
                  <div
                    key={member.id}
                    onClick={() => setSelectedProfileUserId(member.id)}
                    className={`flex items-center justify-between p-3.5 rounded-xl cursor-pointer hover:opacity-80 ${
                      index === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                      index === 1 ? 'bg-slate-400/10 border border-slate-400/30' :
                      index === 2 ? 'bg-orange-500/10 border border-orange-500/30' :
                      'bg-slate-800/50 border border-slate-700/30'
                    } ${member.joined_later ? 'opacity-50' : ''}`}
                    data-testid={`weekly-row-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-500 text-black' :
                        index === 1 ? 'bg-slate-400 text-black' :
                        index === 2 ? 'bg-orange-500 text-black' :
                        'bg-slate-700 text-slate-300'
                      }`}>
                        {member.rank}
                      </span>
                      <div>
                        <span className="text-white font-medium block">
                          {member.team_name || member.display_name || 'Unknown'}
                          {rc === "up" && <span className="text-emerald-400 text-xs font-semibold ml-2">↑{rv}</span>}
                          {rc === "down" && <span className="text-red-400 text-xs font-semibold ml-2">↓{rv}</span>}
                          {rc === "new" && <span className="text-amber-400 text-xs ml-2">NEW</span>}
                          {rc === "same" && <span className="text-slate-600 text-xs ml-2">—</span>}
                        </span>
                        {member.joined_later && (
                          <span className="text-xs text-slate-500">Joined later</span>
                        )}
                      </div>
                    </div>
                    <span className="font-mono font-semibold text-purple-400">
                      {member.weekly_points} pts
                    </span>
                  </div>
                );
              })}

              {weeklyStandings?.cap_awards?.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2 px-1">
                    <Crown size={16} />
                    Week {weeklyStandings.week_number} Caps
                  </h3>
                  {weeklyStandings.cap_awards.map(cap => (
                    <div
                      key={cap.id}
                      className={`p-4 rounded-xl border ${
                        cap.cap_type === 'orange'
                          ? 'bg-orange-500/10 border-orange-500/30'
                          : 'bg-purple-500/10 border-purple-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-wide opacity-70">
                            {cap.cap_type === 'orange' ? 'Orange Cap' : 'Purple Cap'}
                          </div>
                          <div className="text-white font-semibold mt-1">{cap.player_name}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {cap.cap_type === 'orange' ? `${cap.stat_value} runs` : `${cap.stat_value} wickets`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-400">In your league</div>
                          <div className="text-white font-mono text-lg">
                            {cap.awarded_count_in_league} {cap.awarded_count_in_league === 1 ? 'member' : 'members'}
                          </div>
                          <div className="text-xs text-slate-500">earned {cap.points_awarded} pts</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No standings for this week yet</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="pt-2">
        {isCreator ? (
          <button
            onClick={() => setShowAdminPanel(true)}
            className="w-full flex items-center justify-center gap-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-semibold py-3 rounded-xl hover:bg-emerald-500/20 transition-all"
            data-testid="manage-league-btn"
          >
            <Settings size={18} /> Manage League
          </button>
        ) : (
          <button
            onClick={leaveLeague}
            className="w-full flex items-center justify-center gap-2 bg-slate-700 text-white font-semibold py-3 rounded-xl hover:bg-slate-600 transition-all"
            data-testid="leave-league-btn"
          >
            <ExternalLink size={18} /> Leave League
          </button>
        )}
      </div>

      {/* Admin Panel Modal */}
      {showAdminPanel && league && (
        <LeagueAdminPanel 
          league={{...league, leaderboard}} 
          onUpdate={fetchLeagueDetails} 
          onClose={() => {
            setShowAdminPanel(false);
            // Re-check if user is still a member (in case they deleted the league)
            fetchLeagueDetails();
          }} 
        />
      )}

      {/* User Profile Modal */}
      {selectedProfileUserId && (
        <UserProfileCard 
          userId={selectedProfileUserId} 
          onClose={() => setSelectedProfileUserId(null)} 
        />
      )}
    </div>
  );
}
